import { useEffect, useState } from "react";
import {
  API_BASE_URL,
  API_ENDPOINTS,
  MEDIA_BASE_URL,
} from "../config/apiConfig";
import { withPublicUrl } from "./publicUrl";

const withMediaBase = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (MEDIA_BASE_URL && url.startsWith("/")) return `${MEDIA_BASE_URL}${url}`;
  if (url.startsWith("/")) return withPublicUrl(url);
  return url;
};

const withAuth = (init) => {
  const token = process.env.REACT_APP_API_TOKEN;
  if (!token) return init;
  const headers = {
    ...(init?.headers || {}),
    Authorization: `Bearer ${token}`,
  };
  return { ...(init || {}), headers };
};

const withSyncAuth = (init) => {
  const token = process.env.REACT_APP_SYNC_TOKEN || process.env.REACT_APP_API_TOKEN;
  if (!token) return init || {};
  const headers = {
    ...(init?.headers || {}),
    "x-sync-token": token,
  };
  return { ...(init || {}), headers };
};

const fetchJson = async (path, init) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12000);
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, withAuth({
      ...(init || {}),
      signal: controller.signal,
    }));
    if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
};

const fetchJsonWithFallback = async (path, fallbackValue, init) => {
  try {
    return await fetchJson(path, init);
  } catch (error) {
    const message = String(error?.message || "");
    const isRecoverable =
      error?.name === "AbortError" ||
      /Failed to fetch/i.test(message) ||
      /API (5\d\d|0):/.test(message);

    if (!isRecoverable) {
      throw error;
    }

    return fallbackValue;
  }
};

const fetchAdminJson = async (path, init) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, withSyncAuth({
      ...(init || {}),
      signal: controller.signal,
    }));
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || `API ${res.status}: ${path}`);
    }
    return data;
  } finally {
    clearTimeout(timeout);
  }
};

export const getBlogPosts = () => fetchJson(API_ENDPOINTS.blogPosts);

export const getAbout = () => fetchJson(API_ENDPOINTS.about);
export const getContactCaptcha = () => fetchJson(API_ENDPOINTS.contactCaptcha);
export const submitContactForm = (payload) =>
  fetchJson(API_ENDPOINTS.contactSubmit, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload || {}),
  });

// TikTok on hold.
// const TIKTOK_VIDEOS_PATH = "/tiktok/videos";
// export const getTikTokVideos = () => fetchJsonWithFallback(TIKTOK_VIDEOS_PATH);

export const getInstagramPosts = () =>
  fetchJsonWithFallback(API_ENDPOINTS.instagramPosts, { data: [], meta: { count: 0 } });

export const getYouTubePosts = () =>
  fetchJsonWithFallback(API_ENDPOINTS.youtubePosts, { data: [], meta: { count: 0 } });
export const getAdminServices = () => fetchAdminJson(API_ENDPOINTS.adminServices);
export const startAdminService = (serviceId) =>
  fetchAdminJson(API_ENDPOINTS.adminServiceStart(serviceId), { method: "POST" });
export const stopAdminService = (serviceId) =>
  fetchAdminJson(API_ENDPOINTS.adminServiceStop(serviceId), { method: "POST" });
export const getAdminSyncStatus = () => fetchAdminJson(API_ENDPOINTS.adminSyncStatus);
export const getAdminSyncActivity = () => fetchAdminJson(API_ENDPOINTS.adminSyncActivity);
export const getAdminSyncChanges = () => fetchAdminJson(API_ENDPOINTS.adminSyncChanges);
export const triggerAdminSyncNotion = () =>
  fetchAdminJson(API_ENDPOINTS.adminSyncNotion, { method: "POST" });
export const triggerAdminSyncMeta = () =>
  fetchAdminJson(API_ENDPOINTS.adminSyncMeta, { method: "POST" });
export const triggerAdminSyncYouTube = () =>
  fetchAdminJson(API_ENDPOINTS.adminSyncYouTube, { method: "POST" });
export const triggerAdminSyncAll = () =>
  fetchAdminJson(API_ENDPOINTS.adminSyncAll, { method: "POST" });

export const getBlogPostBySlug = async (slug) => {
  if (!slug) return null;

  try {
    const direct = await fetchJson(API_ENDPOINTS.blogPostBySlug(slug));
    const item = direct?.data || direct;
    if (item) return mapBlogPost(item);
  } catch (_) {
    // Continue to list fallback below.
  }

  try {
    const res = await fetchJson(API_ENDPOINTS.blogPosts);
    const list = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res)
        ? res
        : [];
    const item = list.find((entry) => entry?.slug === slug);
    if (item) return mapBlogPost(item);
  } catch (_) {}

  return null;
};

export const getAllBlogPosts = async () => {
  const res = await getBlogPosts();
  let data = Array.isArray(res)
    ? res
    : Array.isArray(res?.data)
      ? res.data
      : [];

  return { data, meta: res?.meta || null };
};

const resolveMediaUrl = (media) => {
  if (!media) return "";
  if (typeof media === "string") return withMediaBase(media);
  if (typeof media !== "object") return "";
  if (typeof media.url === "string") return withMediaBase(media.url);
  return "";
};

const resolveMediaArray = (media) => {
  if (!media) return [];
  if (Array.isArray(media)) {
    return media.map((item) => resolveMediaUrl(item)).filter(Boolean);
  }
  const resolved = resolveMediaUrl(media);
  return resolved ? [resolved] : [];
};

const parseTags = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
    return value
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
};

const resolveCategoryName = (data) => {
  if (typeof data?.category === "string") return data.category;
  if (typeof data?.cat === "string") return data.cat;
  const catData = data?.category;
  return catData?.name || "";
};

const resolveTagNames = (data) => {
  const tagData = data?.tags;
  if (Array.isArray(tagData)) {
    return tagData.map((tag) => (typeof tag === "string" ? tag : tag?.name || "")).filter(Boolean);
  }
  return parseTags(data?.tags);
};

const coerceBoolean = (value) => {
  if (value === true || value === false) return value;
  if (value == null) return false;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "y"].includes(normalized)) return true;
    if (["0", "false", "no", "n"].includes(normalized)) return false;
  }
  return Boolean(value);
};

const normalizeWhitespace = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const normalizeSlugKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/-\d+$/, "")
    .trim();

const getBlogPostDedupKey = (post) => {
  const titleKey = normalizeWhitespace(post?.title);
  if (titleKey) return `title:${titleKey}`;
  const slugKey = normalizeSlugKey(post?.slug || post?.id || "");
  return slugKey ? `slug:${slugKey}` : "";
};

const getBlogPostPriority = (post) => {
  const time = Date.parse(post?.publishedAt || post?.createdAt || "");
  const parsedTime = Number.isNaN(time) ? 0 : time;
  const imageUrl = String(post?.imgeUrl || "");
  const hasLocalMirroredImage = imageUrl.startsWith("/assets/img/blog/");
  const hasLegacyUploadImage = imageUrl.startsWith("/uploads/");
  return (
    parsedTime +
    (hasLocalMirroredImage ? 2 : 0) +
    (hasLegacyUploadImage ? -1 : 0)
  );
};

export const dedupeBlogPosts = (posts) => {
  const chosen = new Map();

  (posts || []).forEach((post) => {
    const key = getBlogPostDedupKey(post);
    if (!key) return;

    const existing = chosen.get(key);
    if (!existing) {
      chosen.set(key, post);
      return;
    }

    if (getBlogPostPriority(post) >= getBlogPostPriority(existing)) {
      chosen.set(key, post);
    }
  });

  return Array.from(chosen.values());
};

const mapBlogPost = (item) => {
  const data = item || {};
  const publishedAt =
    data.published_at ||
    data.publishedAt ||
    data.date ||
    data.created_date ||
    data.createdAt;
  const createdAt =
    data.created_date ||
    data.createdDate ||
    data.created_at ||
    data.createdAt ||
    data.created ||
    publishedAt;
  const published = publishedAt
    ? new Date(publishedAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";
  const created = createdAt
    ? new Date(createdAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const imageUrl = resolveMediaUrl(data.imageUrl || data.hero_image_url);

  const category = resolveCategoryName(data);
  const tags = resolveTagNames(data);

  return {
    id: data.id,
    slug: data.slug || String(data.id || ""),
    title: data.title || data.seoTitle || data.seo_title || "",
    subtitle: data.subtitle || "",
    content:
      data.excerpt || data.seoExcerpt || data.seo_excerpt || data.content || "",
    body: data.content || data.body || data.excerpt || "",
    excerpt: data.excerpt || "",
    imgeUrl: imageUrl,
    cat: category,
    categories: Array.isArray(data.categories)
      ? data.categories
      : category
        ? [category]
        : [],
    featured: coerceBoolean(data.featured),
    published,
    publishedAt,
    created,
    createdAt,
    seed: data.slug || `post-${data.id}`,
    references: data.references || [],
    blocks: data.blocks || [],
    tags,
  };
};

const mapAbout = (item) => {
  const data = item || {};

  const heroImages = resolveMediaArray(data.hero_images || data.heroImages);

  const chaptersSource = Array.isArray(data.chapters) ? data.chapters : [];

  const chapters = chaptersSource.map((chapter) => {
    const entry = chapter || {};
    return {
      title: entry.title || "",
      subtitle: entry.subtitle || "",
      body: entry.body || "",
      quote: entry.quote || "",
      image_url: resolveMediaUrl(entry.image || entry.image_url || entry.imageUrl),
      footer_cta_label: entry.footer_cta_label || entry.footerCtaLabel || "",
      footer_cta_href: entry.footer_cta_href || entry.footerCtaHref || "",
    };
  });

  return {
    heroTitle: data.hero_title || data.heroTitle || "",
    heroSubtitle: data.hero_subtitle || data.heroSubtitle || "",
    heroImages,
    chapters,
  };
};

export const useBlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchFromDb = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getAllBlogPosts();
        const data = Array.isArray(res?.data) ? res.data : [];
        const mapped = data
          .map((item) => mapBlogPost(item))
          .filter((p) => Boolean(p.title));
        const deduped = dedupeBlogPosts(mapped);
        if (!cancelled) {
          setPosts(deduped);
        }
      } catch (e) {
        console.error("Failed to fetch blog posts from DB", e);
        if (!cancelled) {
          setError(e);
          setPosts([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchFromDb();

    return () => {
      cancelled = true;
    };
  }, []);

  return { posts, loading, error };
};

export const useAbout = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAbout = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAbout();
        const raw = data?.data ?? data;
        const item = Array.isArray(raw) ? raw[0] : raw;
        const mapped = item ? mapAbout(item) : null;
        if (!cancelled) {
          setAbout(mapped);
        }
      } catch (e) {
        console.error("Failed to fetch about from DB", e);
        if (!cancelled) {
          setError(e);
          setAbout(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAbout();

    return () => {
      cancelled = true;
    };
  }, []);

  return { about, loading, error };
};

// export const useTikTokVideos = () => {
//   const [videos, setVideos] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//
//   useEffect(() => {
//     let cancelled = false;
//
//     const fetchVideos = async () => {
//       try {
//         setLoading(true);
//         setError(null);
//         const { data } = await getTikTokVideos();
//         const payload = data?.data || data;
//         const list = Array.isArray(payload?.videos) ? payload.videos : [];
//         if (!cancelled) {
//           setVideos(list);
//         }
//       } catch (e) {
//         console.error("Failed to fetch TikTok videos", e);
//         if (!cancelled) {
//           setError(e);
//           setVideos([]);
//         }
//       } finally {
//         if (!cancelled) {
//           setLoading(false);
//         }
//       }
//     };
//
//     fetchVideos();
//
//     return () => {
//       cancelled = true;
//     };
//   }, []);
//
//   return { videos, loading, error };
// };

export const useInstagramPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getInstagramPosts();
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        const mapped = list.map((item) => mapInstagramPost(item));
        if (!cancelled) {
          setPosts(mapped);
        }
      } catch (e) {
        console.error("Failed to fetch Instagram posts", e);
        if (!cancelled) {
          setError(e);
          setPosts([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      cancelled = true;
    };
  }, []);

  return { posts, loading, error };
};

export const useYouTubePosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getYouTubePosts();
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        const mapped = list.map((item) => mapYouTubePost(item));
        if (!cancelled) {
          setPosts(mapped);
        }
      } catch (e) {
        console.error("Failed to fetch YouTube posts", e);
        if (!cancelled) {
          setError(e);
          setPosts([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      cancelled = true;
    };
  }, []);

  return { posts, loading, error };
};

const mapInstagramPost = (item) => {
  const data = item || {};
  return {
    id: data.id,
    title: data.title || "",
    caption: data.caption || "",
    media_url: resolveMediaUrl(data.media_url) || "",
    thumbnail_url: resolveMediaUrl(data.thumbnail_url) || "",
    permalink: data.permalink || "",
    timestamp: data.timestamp || "",
    media_type: data.media_type || "",
  };
};

const mapYouTubePost = (item) => {
  const data = item || {};
  return {
    id: data.id,
    yt_id: data.yt_id || data.id || "",
    title: data.title || "",
    description: data.description || "",
    video_published_at: data.video_published_at || "",
    thumbnail_url: resolveMediaUrl(data.thumbnail_url) || "",
    video_url: data.video_url || "",
    channel_title: data.channel_title || "",
    raw: data.raw || null,
  };
};
